import * as React from 'react';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Dashboard from '../../../../components/Admin/Dashboard.js';
import FacultyDashboard from '../../../../components/Faculty/Dashboard';
import StudentDashboard from '../../../../components/Student/Dashboard';
import JudgeDashboard from '../../../../components/Judge/Dashboard';
import { useState, useEffect } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Styles } from '../../../../styles/global.js';
import {
  deleteRubric,
  getRubric,
  updateRubric,
} from '../../../../utils/API.js';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Breadcrumbs,
  Button,
  Typography,
} from '@mui/material';

import RubricEntity from '../../../../components/Entity/SearchRubric.js';
import AdvancedModal from '../../../../components/AdvancedModal.js';
import ViewRubric from '../../../../components/Rubrics/ViewRubric.js';
import RubricForm from '../../../../components/Rubrics/index.js';
import useFeedback from '../../../../utils/FeedbackContext/useFeedback.js';
import { useAuth } from '../../../../utils/AuthContext.js';
import { ExpandMore } from '@mui/icons-material';
import SearchProject from '../../../../components/Entity/SearchProject.js';
import { AwardIcon } from '../../../../components/AwardIcon.js';

const Awards = ({ awards }) => {
  if (!awards || awards.length === 0) {
    return <Typography>No awards associated with this rubric.</Typography>;
  }

  // fragment awards based on year, then sort by year descending
  const awardsByYear = awards.reduce((acc, award) => {
    const year = award.year || 'Unknown Year';
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(award);
    return acc;
  }, {});

  const sortedYears = Object.keys(awardsByYear).sort((a, b) => b - a);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Associated Awards
      </Typography>
      {sortedYears.map((year) => (
        <Accordion key={year} mb={2}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>{year} Awards</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {awardsByYear[year].map((award) => (
              <Box key={award._id} mb={2} p={2} border={1} borderRadius={2}>
                <Box display="flex" alignItems="center" mb={1} gap={1}>
                  <AwardIcon icon={award.icon} />
                  <Typography variant="subtitle1">{award.title}</Typography>
                </Box>
                <Typography variant="body2">{award.description}</Typography>
                {award.projectDetails && (
                  <SearchProject project={award.projectDetails} />
                )}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

function Actions({ rubric, setRubric }) {
  const [deleteModalShowing, setDeleteModalShowing] = useState(false);
  const [editModalShowing, setEditModalShowing] = useState(false);
  const [error, setError] = useState();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { notifySuccess, notifyError } = useFeedback();
  const handleEdit = async () => {
    try {
      await updateRubric(rubric?._id, rubric);
      notifySuccess('Rubric edited successfully');
      setError(null);
      setEditModalShowing(false);
    } catch (error) {
      console.error('Error Editing Rubric:', error);
      notifyError(
        'Error while Editing Rubric, check error message for more details',
      );
      setError(error?.response?.data || 'Error while Editing Rubric');
    }
  };

  const handleDeletion = async () => {
    try {
      await deleteRubric(rubric?._id);
      notifySuccess('Rubric deleted successfully');
      navigate(`/admin/dashboard/rubrics?${searchParams.toString()}`);
      setDeleteModalShowing(false);
    } catch (error) {
      console.error('Error deleting Rubric:', error);
      notifyError('Error deleting Rubric');
    }
  };
  return (
    <>
      {rubric && (
        <Box display="flex" justifyContent={'end'}>
          <Box>
            <Button color="primary" onClick={() => setEditModalShowing(true)}>
              Edit
            </Button>
            <Button color="error" onClick={() => setDeleteModalShowing(true)}>
              Delete
            </Button>
          </Box>
          <AdvancedModal
            open={deleteModalShowing}
            title="Delete Rubric"
            handleClose={() => setDeleteModalShowing(false)}
            handleSave={handleDeletion}
          >
            <Alert severity="error">
              Are you sure you want to delete this rubric? Click Save to
              confirm.
            </Alert>
          </AdvancedModal>
          <AdvancedModal
            open={editModalShowing}
            title="Edit Rubric"
            handleClose={() => setEditModalShowing(false)}
            handleSave={handleEdit}
            width="80vw"
          >
            <RubricForm rubric={rubric} setRubric={setRubric} />
            {error && (
              <Alert severity="error" sx={{ marginY: 1 }}>
                {error}
              </Alert>
            )}
          </AdvancedModal>
        </Box>
      )}
    </>
  );
}

// Page that shows when a project is already created
const ExistingRubricDash = () => {
  const [rubric, setRubric] = useState(null);
  const { rid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  let role = user?.role?.toLowerCase();

  let link1 = '#';
  let link2 = '#';

  const { pid } = useParams();

  if (role === 'admin') {
    link1 = `/admin/dashboard?year=${new Date().getFullYear()}`;
    link2 = `/admin/dashboard/rubrics?${searchParams.toString()}`;
  } else if (role === 'faculty') {
    link1 = `/faculty/dashboard?year=${new Date().getFullYear()}&department=${
      user?.department
    }&course=SD2`;
    link2 = `/faculty/dashboard/rubrics?year=${new Date().getFullYear()}&department=${
      user?.department
    }`;
  } else if (role === 'student') {
    link1 = `/student/dashboard/project/${pid}`;
    link2 = `/student/dashboard/project/${pid}/rubrics`;
  } else if (role === 'judge') {
    link1 = `/judge/dashboard`;
    link2 = `/judge/dashboard/rubrics`;
  } else {
    // nothing
  }

  const isAdmin = role === 'admin';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRubric(rid);
        setRubric(response.data.data);
      } catch (error) {
        navigate('/');
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [rid, navigate]);

  return (
    <>
      <Box {...Styles.mainBox}>
        <Toolbar />

        {/* Container With Max Width = lg breakpoint */}
        <Container {...Styles.customContainer()}>
          {/*Outer Grid Container */}
          <Grid container spacing={3}>
            {/*Inner Grid Begins*/}

            {/*Inner Grid Item -1: Actions */}
            {role === 'admin' && (
              <Grid item lg={12} sm={12} xs={12}>
                <Actions rubric={rubric} setRubric={setRubric} />
              </Grid>
            )}

            {/*Inner Grid Item 0: BreadCrumbs */}
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Box>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link to={link1}>Dashboard</Link>
                  <Link to={link2}>Rubrics</Link>
                  <Typography color="textPrimary">Rubric</Typography>
                </Breadcrumbs>
              </Box>
            </Grid>

            {/*Inner Grid Item 1: Page Title */}
            <Grid item lg={12} sm={12} xs={12}>
              <Paper {...Styles.dashboardPageWidget()}>
                Rubric Dashboard View
              </Paper>
            </Grid>

            {/*Inner Grid Item 2: Rubric Details */}
            <Grid item lg={12} sm={12} xs={12}>
              <Paper {...Styles.dashboardContentWidget('auto')}>
                <>
                  <RubricEntity rubric={rubric} expandDescription={true} />
                  <Box padding={2}>
                    <ViewRubric rubric={rubric} />
                  </Box>
                </>
              </Paper>
            </Grid>

            {isAdmin && (
              <Grid item lg={12} sm={12} xs={12}>
                <Awards awards={rubric?.awards} />
              </Grid>
            )}

            {/*End of Inner Grid*/}
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default function AdminDashboardRubric() {
  const { user } = useAuth();

  if (!user) {
    return <></>;
  }

  return (
    <>
      {user?.role === 'ADMIN' && (
        <Dashboard
          Page={ExistingRubricDash}
          PageName="(A) Dashboard - Rubric"
        />
      )}
      {user?.role === 'FACULTY' && (
        <FacultyDashboard
          Page={ExistingRubricDash}
          PageName="(F) Dashboard - Rubric"
        />
      )}
      {user?.role === 'STUDENT' && (
        <StudentDashboard
          Page={ExistingRubricDash}
          PageName="(S) Dashboard - Rubric"
        />
      )}
      {user?.role === 'JUDGE' && (
        <JudgeDashboard
          Page={ExistingRubricDash}
          PageName="(J) Dashboard - Rubric"
        />
      )}
    </>
  );
}
